import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {generateResetPinLink} from '../../api/productService';
import './ForgotPin.css';
import '../Login/Login.css';
import ViewMessage from "../../components/ViewMessage";

const ForgotPin = () => {
  const {t} = useTranslation();
  const [email, setEmail] = useState(sessionStorage.getItem('email') || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const navigate = useNavigate();
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Scroll the page up if the bottom actions block intersects with the login form content
  useEffect(() => {
    const actionsEl = actionsRef.current;
    const formEl = formRef.current;
    if (!actionsEl || !formEl) return;


    const maybeScroll = () => {
      document.querySelector('.content')?.scrollBy({ top: 400, left: 0, behavior: 'smooth' });
    };

    // Observe element resizes (layout changes, keyboard, fonts) using ResizeObserver
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        // Debounce slightly to allow layout to settle
        setTimeout(maybeScroll, 50);
      });
      ro.observe(actionsEl);
      ro.observe(formEl);
    }

    // Observe viewport resize/orientation changes
    const onResize = () => setTimeout(maybeScroll, 300);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    // Try on mount as well
    setTimeout(maybeScroll, 0);
    setTimeout(maybeScroll, 250);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // When email input focuses (mobile keyboard shows), ensure actions are visible
  useEffect(() => {
    const input = document.getElementById('email');
    if (!input) return;
    const handler = () => {
      // small delay to allow viewport resize due to keyboard
      setTimeout(() => {
        if (actionsRef.current) {
          actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 150);
    };
    input.addEventListener('focus', handler);
    return () => input.removeEventListener('focus', handler);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError(t('pin.forgot.email_required'));
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('pin.forgot.email_invalid'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await generateResetPinLink(email);
      setSuccess(true);
      setShowMessage(true);
    } catch (error: any) {
      setError(error.response?.data?.message || t('pin.forgot.send_failed'));
      setShowMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };


  return (
    <div className="login-container forgot-pin-page">
      <ViewMessage
        show={showMessage}
        type={success ? 'success' : 'error'}
        title={success ? t('pin.forgot.success_title') : 'Oops!'}
        text={success ? t('pin.forgot.success_message') : error}
        onClose={() => setShowMessage(false)}
      />
      <form className="login-form" onSubmit={handleSubmit} ref={formRef}>
        <h1 className="login-title">{t('pin.forgot.title')}</h1>
        <div className="login-subtitle">{t('pin.forgot.instructions')}</div>

        <div className="login-email-label">{t('pin.forgot.email_label')}</div>
        <input
          id="email"
          type="email"
          className="forgot-pin-input"
          placeholder={t('pin.forgot.email_placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          autoComplete="off"
          required
        />


        <div className="forgot-pin-actions-block" ref={actionsRef}>
          <div className="forgot-pin-actions">
            <span>{t('pin.forgot.return_to_login')}</span>
            <button
              type="button"
              className="return-pin-login-link"
              onClick={handleBackToLogin}
            >
              {t('pin.forgot.login_link_short') || 'Log In'}
            </button>
          </div>

          <button
            type="submit"
            className="forgot-submit-btn"
            disabled={isLoading || !email.trim() || success}
          >
            {isLoading ? t('pin.forgot.sending') : t('pin.forgot.submit_button')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ForgotPin;
